import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOrgStore, useIsVerne, useEffectiveOrgId, persistSelectedOrgId } from '@/stores/useOrgStore';
import { OrgBadge } from '@/components/nav/OrgBadge';
import { cn } from '@/lib/utils';

// Org switcher — only rendered for verne admins (org_id === 1) once the
// /orgs list has loaded. Selection is persisted in localStorage by the store.
export function OrgSelector() {
  const isVerne = useIsVerne();
  const orgs = useOrgStore((s) => s.orgs);
  const effectiveOrgId = useEffectiveOrgId();

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRight, setDropdownRight] = useState(0);
  const [dropdownTop, setDropdownTop] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownRight(window.innerWidth - rect.right);
      setDropdownTop(rect.bottom + 8);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !(e.target as Element).closest('[data-org-selector-dropdown]')
      ) {
        setIsOpen(false);
      }
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [isOpen]);

  // Non-verne users — and verne users while the org list is still loading —
  // get a read-only badge instead of the switcher.
  if (!isVerne || orgs.length === 0) return <OrgBadge />;

  const active = orgs.find((o) => o.id === effectiveOrgId) ?? null;
  const label = active?.name ?? 'Select org';
  const short = label.length > 18 ? `${label.slice(0, 16)}…` : label;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors select-none',
          isOpen ? 'bg-accent/10 text-accent' : 'text-muted hover:text-foreground',
        )}
        title={label}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
          <path d="M3 21h18" />
          <path d="M5 21V7l7-4 7 4v14" />
          <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
        </svg>
        {short}
        <svg
          className={cn('transition-transform', isOpen ? 'rotate-180' : '')}
          width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          data-org-selector-dropdown
          className="fixed z-[100] min-w-56 max-h-[60vh] overflow-y-auto rounded-xl border border-border bg-surface/95 backdrop-blur-sm shadow-lg py-1"
          style={{ top: dropdownTop, right: dropdownRight }}
        >
          <div className="px-3 py-1.5 text-[11px] font-medium text-muted uppercase tracking-wider border-b border-border mb-1">
            Organization
          </div>
          {orgs.map((org) => {
            const isActive = org.id === effectiveOrgId;
            return (
              <button
                key={org.id}
                onClick={() => {
                  setIsOpen(false);
                  if (!isActive) {
                    // Persist the selection without touching React state —
                    // updating the store would re-run RobotsGate's effect
                    // with the new orgId on the still-mounted page and race
                    // the URL replace below. The store re-initializes from
                    // localStorage on the reload.
                    persistSelectedOrgId(org.id);
                    // Drop ?robot_id= — it refers to a robot in the previous
                    // org. Setting the URL to a path with no robot_id
                    // (instead of replacing the current href) guarantees a
                    // hard navigation, not a same-document hash update.
                    const url = new URL(window.location.href);
                    url.searchParams.delete('robot_id');
                    window.location.href = url.toString();
                  }
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors',
                  isActive
                    ? 'text-accent bg-accent/5'
                    : 'text-foreground hover:bg-surface-raised',
                )}
              >
                <span className="truncate flex-1">{org.name}</span>
                {isActive && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="shrink-0 text-accent">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
